import DEFAULT_SPEC from '../spec.json' with { type: 'json' };
import pjson from '../../package.json' with { type: 'json' };

let env = null;
let agentPromise = null;
let additionalRequestHeaders = null;
if (// we are not in node, as there is no node version set, or...
!process?.versions?.node || // we are in node, but we are running Jest tests
typeof process?.env?.JEST_WORKER_ID !== 'undefined' // eslint-disable-line no-process-env
) {
    // we're in a browser
    env = 'browser';
    // there is no need for an agent with browser fetch
    agentPromise = Promise.resolve(null);
    additionalRequestHeaders = {};
} else {
    // we're in node
    env = 'node';
    // Establish an agent to allow for disabling keepAlive via the keepAlive option
    // NOTE: It is already enabled by default in the default Agent used by native fetch.
    agentPromise = import('undici').then(({ Agent })=>{
        return new Agent({
            connect: {
                keepAlive: false
            }
        });
    });
    additionalRequestHeaders = {
        'User-Agent': `OpenPath-HeliumClient/${pjson.version} ${env} (+engineering@openpath.com)`
    };
}
// Adding this typedef here instead of in generateHeliumTypes template because this
// type can be used in the future to document private functions (e.g. _GET, _POST, ...)
/**
 * @typedef {Object} HeliumRequestOptions
 * @property {boolean} [firePostRequestCallback] If set to `false`, this request will not execute the postRequestCallback defined when the client instance was created
 * @property {Record<string,string>} [headers] Object with headers to be added to the request
 * @property {{url: string}} [overrides] Object with overrides for the fetch request. Currently only supports overriding the URL
 */ class HeliumClientError extends Error {
    constructor(what, response, expected){
        let msg = what;
        if (response.statusCode !== expected) {
            msg += ` returned ${response.statusCode} (expected ${expected})`;
        }
        super(msg);
        Error.captureStackTrace(this, this.constructor);
        this.name = this.constructor.name;
        this.status = response.statusCode;
        this.expected = expected;
        this.json = response.json;
    }
}
// Exported for mock access to static _getAgent()
class LiveServer {
    // HeliumClient was originally built as a wrapper around
    // server.inject(); here, we are instead calling a live endpoint
    // over the network, using native fetch, so this class's
    // purpose is to translate the request/response objects
    // appropriately
    constructor(endpoint, { keepAlive = false } = {}){
        this.endpoint = endpoint;
        this.keepAlive = keepAlive;
    }
    // LiveServer._getAgent() exists solely to be able to override the returned agent in unit tests
    static async _getAgent() {
        return await agentPromise;
    }
    async inject(origRequest, overrides = {}) {
        // transform the request from what server.inject() normally
        // expects into what fetch expects
        const request = {
            method: origRequest.method,
            headers: {
                ...origRequest.headers || {},
                ...additionalRequestHeaders
            }
        };
        const agent = !this.keepAlive ? await LiveServer._getAgent() : null;
        if (agent) {
            request.dispatcher = agent;
        }
        if (origRequest.payload) {
            request.body = origRequest.payload.constructor?.name === 'FormData' ? origRequest.payload // FormData should just go as-is
             : JSON.stringify(origRequest.payload);
        }
        const url = overrides.url || `${this.endpoint}${origRequest.url}`;
        // transform the response from fetch into what a server.inject()
        // response normally looks like
        const origResponse = await fetch(url, request);
        const statusCode = origResponse.status;
        const response = {
            statusCode,
            headers: Object.fromEntries(origResponse.headers.entries())
        };
        try {
            if (response.headers['content-type'] === 'application/xml') response.payload = await origResponse.text();
            else response.json = statusCode !== 204 ? await origResponse.json() : null;
        } catch  {
        // suppress error here... lack of json in the response will lead
        // to a more appropriate error in later processing
        }
        return response;
    }
}
function generateQueryString(queryParams) {
    let queryString = '?';
    if (queryParams) {
        Object.keys(queryParams).forEach((key)=>{
            const rawValue = queryParams[key];
            if (Array.isArray(rawValue)) {
                // for an array (key: [1,2,3]), we encode into the query
                // string as "key=1&key=2&key=3", following the approach in
                // https://nodejs.org/docs/latest-v12.x/api/querystring.html#querystring_querystring_stringify_obj_sep_eq_options
                // and note this encoding is also natively parsed back into an
                // array by hapi's parser
                rawValue.forEach((elem)=>{
                    const value = encodeURIComponent(elem);
                    const pair = `${key}=${value}&`;
                    queryString += pair;
                });
            } else {
                const value = encodeURIComponent(rawValue);
                const pair = `${key}=${value}&`;
                queryString += pair;
            }
        });
    }
    queryString = queryString.slice(0, -1); // removes the last &, or removes ? if no params
    return queryString;
}
class HeliumClient {
    /**
   * @param server {LiveServer}
   * @param [token = null] {string | null}
   * @param [headers = {}] {Record<string,string>}
   * @param [postRequestCallback = null] {null | ((code: number, response: { statusCode: number, json: {message: string}}) => Promise<void>)}
   * @returns {HeliumClient}
   */ constructor(server, token = null, headers = {}, postRequestCallback = null){
        // SWAGGER NOTE: swaggerOp() dynamically translates from a
        // javascript args list into the underlying http call, using
        // metadata from the swagger info about the given
        // operationId...
        /**
   * @private
   */ this.swaggerOp = async (operationId, args = [])=>{
            if (!this.spec) this.spec = DEFAULT_SPEC;
            const op = this.spec[operationId];
            if (!op) throw new Error(`Unknown operationId ${operationId}`);
            const [method, pathTemplate, parameters, responseCode, withPayload, withQuery] = op;
            // console.log('%%%%%%%%%%%%%%% swaggerOp', operationId, {method, pathTemplate, parameters, responseCode, withPayload, withQuery}, args)
            let path = pathTemplate;
            for (const p of parameters){
                path = path.replace(`{${p}}`, args.shift());
            }
            const payload = withPayload ? args.shift() : null;
            const queryParams = withQuery ? args.shift() : null;
            let result = null;
            if (args.length) {
                const flags = args.shift();
                result = await this._request(responseCode, method, path, payload, queryParams, flags);
            } else {
                result = await this._request(responseCode, method, path, payload, queryParams);
            }
            return result;
        };
        /**
   * Inject headers to requests
   * @param [headers = {}] {Record<string,string>}
   * @returns {HeliumClient | HeliumRawClient} a new instance with headers added
   */ this.withHeaders = (headers = {})=>{
            const ret = new HeliumClient(this.server, this.token, {
                ...this.headers,
                ...headers
            }, this.postRequestCallback);
            ret.isRaw = this.isRaw;
            return ret;
        };
        /**
   * Returns a more complete response, with HTTP status code
   * @param [headers = {}] {Record<string,string>}
   * @returns {HeliumRawClient} a new instance where all requests are more complete
   */ this.raw = (headers = {})=>{
            const ret = new HeliumClient(this.server, this.token, {
                ...this.headers,
                ...headers
            }, this.postRequestCallback);
            ret.isRaw = true;
            return ret;
        };
        /**
   * Set auth token for requests
   * @param token {string}
   */ this.setToken = (token)=>{
            this.token = token;
        };
        /**
   * @param method {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'}
   * @param url {string}
   * @param [payload = null] {object |null}
   * @param [queryParams = {}] {Record<string, string>}
   * @param [flags = {headers: {}, firePostRequestCallback: true] {{headers: Record<string,string>, firePostRequestCallback: boolean}}
   * @param expectCode {number|null}
   * @returns {Promise<{headers: ({}|Headers), statusCode: number}|null|*>}
   */ this.requestExternal = async (method, url, payload = null, queryParams = {}, flags = {
            headers: {},
            firePostRequestCallback: true
        }, expectCode = null)=>{
            return await this._request(expectCode, method, url, payload, queryParams, flags);
        };
        /** @private */ this._request = async (expectCode, method, urlpath, payload, queryParams, flags = {
            firePostRequestCallback: true,
            overrides: {},
            headers: {}
        })=>{
            const queryString = generateQueryString(queryParams);
            // @TODO @TEMP - Something is wrong here, I believe it has
            // to do with the different function parameters in _GET vs _POST
            // and how its called from Platinum. This should work fow now -justin
            const { overrides, headers, firePostRequestCallback = true } = flags || {
                firePostRequestCallback: true,
                overrides: {},
                headers: {}
            };
            // prevent people (me) from calling ._GET, ._POST, etc. with the wrong argument signature
            if (expectCode && isNaN(expectCode)) {
                throw new HeliumClientError('Must pass expectCode as first param');
            }
            const request = {
                method: method,
                url: urlpath + queryString,
                headers: {
                    Accept: 'application/json, application/xml',
                    ...this.headers,
                    ...headers
                }
            };
            // don't inject auth header if we're overriding the URL, as this means
            // we are reaching out to an external resource
            if (this.token && !overrides?.url) {
                request.headers.Authorization = this.token;
            }
            if (payload) {
                if (payload.constructor?.name === 'FormData') {
                    if (this.server instanceof LiveServer) {
                        // Will ultimately call fetch, so multipart/form-data will be set automatically with a boundary
                        delete request.headers['Content-Type'];
                        request.payload = payload;
                    } else {
                        // We are probably in Hapi using a real server.inject and require('form-data'),
                        // so it works a little differently...
                        request.headers = {
                            ...request.headers,
                            ...payload.getHeaders()
                        };
                        request.payload = payload.getBuffer();
                    }
                } else {
                    request.headers['Content-Type'] = 'application/json';
                    request.payload = payload;
                }
            }
            const r = await this.server.inject(request, overrides);
            if (!r.json && r.payload) {
                if (r.payload[0] === '[' || r.payload[0] === '{') // maybe this should be a test on r.headers['content-type'] === 'application/json' instead?
                r.json = JSON.parse(r.payload);
                else if (r.headers['content-type'] === 'application/xml') // we have to put the xml string in r.json.data, because
                // r.json.data is what we later return from this function
                r.json = {
                    data: r.payload
                };
            }
            // check token validity
            if (Boolean(firePostRequestCallback) && this.postRequestCallback) {
                await this.postRequestCallback(expectCode, r);
            }
            if (this.isRaw) {
                return r;
            }
            // when exposing internal request, we may not pass an expect code
            if (expectCode && r.statusCode !== expectCode) {
                throw new HeliumClientError(`${method} ${urlpath}`, r, expectCode);
            }
            if (r.statusCode === 204) {
                // no response body on a 204
                return null;
            }
            if (r.json?.filteredCount) {
                const limit = queryParams?.limit || 10;
                const offset = queryParams?.offset || 0;
                const expectedLength = offset + limit < r.json.filteredCount ? limit : r.json.filteredCount - offset;
                if (r.json.data.length < expectedLength) {
                    throw new HeliumClientError(`${method} ${urlpath} returned too few records in limited response (expected ${expectedLength} of ${r.json.filteredCount} total filtered records, but got ${r.json.data.length})`, r, expectCode);
                }
            // don't error on too many records for now, because we don't
            // have a foolproof way to know if the particular API was in
            // fact limited (need to read swagger.json to know this on a
            // per-API basis)
            }
            return r.json.data;
        };
        /** @private */ this._PUT = (expectCode, urlpath, payload, flags)=>{
            return this._request(expectCode, 'PUT', urlpath, payload, null, flags);
        };
        /** @private */ this._PATCH = (expectCode, urlpath, payload, flags)=>{
            return this._request(expectCode, 'PATCH', urlpath, payload, null, flags);
        };
        /** @private */ this._POST = (expectCode, urlpath, payload, queryParams, flags)=>{
            return this._request(expectCode, 'POST', urlpath, payload, queryParams, flags);
        };
        /** @private */ this._GET = (expectCode, urlpath, queryParams, flags)=>{
            return this._request(expectCode, 'GET', urlpath, null, queryParams, flags);
        };
        /** @private */ this._DELETE = (expectCode, urlpath, flags)=>{
            return this._request(expectCode, 'DELETE', urlpath, null, null, flags);
        };
        /*
   * Auth - this are defined specially instead of just using the
   * standard swaggerOp interceptor, because we need to manipulate
   * this.token during auth operations
   */ /** ignore type def */ this.login = async (payload)=>{
            const data = await this._POST(201, '/auth/login', payload);
            this.token = data.token;
            return data;
        };
        /** ignore type def */ this.logout = async ()=>{
            const data = await this._POST(204, '/auth/logout', {});
            this.token = null;
            return data;
        };
        /** ignore type def */ this.refreshLogin = async ()=>{
            const response = await this._POST(201, `/auth/accessTokens/refresh`, null, null, {
                firePostRequestCallback: false
            });
            if (this.isRaw && response.json?.data) {
                this.token = response.json.data.token;
            } else {
                this.token = response.token;
            }
            return response;
        };
        /*
   * Oddballs - operations that are not in the swagger spec or need to
   * be defined specially for compatibility/transition reasons
   */ this.checkMaintenanceEndpoint = (maintenanceUrl)=>{
            // this endpoint (and the ability to override request/inject variables) was
            // added so that projects that depend on Helium client can eventually
            // remove all networking libraries (request/fetch/node-fetch/etc)
            return this._GET(200, null, null, {
                overrides: {
                    url: maintenanceUrl
                },
                firePostRequestCallback: false
            });
        };
        this.describeSwagger = (queryParams)=>{
            return this._GET(200, '/swagger.json', queryParams);
        };
        this.describeOpenApi = (queryParams)=>{
            return this._GET(200, '/openapi.json', queryParams);
        };
        this.getEmailAlerts = (...args)=>{
            // TODO remove once testsuite is using listEmailAlerts instead
            return this.swaggerOp('listEmailAlerts', args) // see SWAGGER NOTE above
            ;
        };
        /**
     * @private
     * @type {LiveServer}
     */ this.server = server;
        /**
     * @private
     * @type {string|null}
     */ this.token = token;
        /**
     * @private
     * @type {Record<string, string>}
     */ this.headers = headers;
        /**
     * @private
     * @type {boolean}
     */ this.isRaw = false;
        /**
     * @private
     * @type {(function(number, *): Promise<void>)|null}
     */ this.postRequestCallback = postRequestCallback;
        // wrap "this" in a Proxy before we return...
        return new Proxy(this, {
            // NOSONAR
            get: function(target, property, receiver) {
                // idea is we dynamically construct functions to handle the
                // various swagger operationIds... we cache the generated
                // functions so we only have to generate it the first time a
                // particular operationId is used, but we also do it lazily so
                // that HeliumClient construction/startup doesn't incur a big
                // cost by constructing all of the operationId functions at
                // once
                if (property in target) // if it's a regular property of the target HeliumClient
                // object, or if we've already generated+cached the function
                // for the property/operationId, then just return that
                return target[property];
                // not already found in the target HeliumClient object, so
                // load up our swagger mini-spec so we can check it to see of
                // the property exists there as an operationId
                if (!target.spec) {
                    target.spec = DEFAULT_SPEC;
                    // Allow reading the spec property directly, even when it hadn't been loaded yet
                    if (property === 'spec') {
                        return target.spec;
                    }
                }
                const operationId = property;
                if (target.spec[operationId]) {
                    const f = (...args)=>target.swaggerOp(operationId, args);
                    target[operationId] = f;
                    return f;
                }
                return void 0;
            }
        });
    }
    /**
   * @overload
   * @param endpoint {string}
   * @param [token = null] {string | null}
   * @param [postRequestCallback = null] {null | ((code: number, response: { statusCode: number, json: {message: string}}) => Promise<void>)}
   * @param [isRaw = false] {boolean}
   * @param [options = {}] {object}
   * @param [options.keepAlive = false] {boolean} Reuse HTTP(s) connections?
   * @returns {HeliumClient}
   */ /**
   * @overload
   * @param endpoint {string}
   * @param [token = null] {string | null}
   * @param [postRequestCallback = null] {null | ((code: number, response: { statusCode: number, json: {message: string}}) => Promise<void>)}
   * @param [isRaw] {true}
   * @param [options = {}] {object}
   * @param [options.keepAlive = false] {boolean} Reuse HTTP(s) connections?
   * @returns {HeliumRawClient}
   */ static forLiveServer(endpoint, token = null, postRequestCallback = null, isRaw = false, { keepAlive = false } = {}) {
        const server = new LiveServer(endpoint, {
            keepAlive
        });
        const c = new HeliumClient(server, token, {}, postRequestCallback);
        c.isRaw = isRaw;
        return c;
    }
}

export { HeliumClient, HeliumClientError, LiveServer };
